import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { Typography } from 'interfaces/typography';
import { 
    updateCurrentDispositions,
    selectCurrentDispositions,
    selectIsSaving
} from 'lib/redux/settings/platformDispositionsSlice';

const LIST_DISPOSITION_OPTIONS = Object.freeze({
    NO_ANSWER: 'No Answer',
    LEFT_VOICEMAIL: 'Left Voicemail',
    NOT_IN_SERVICE: 'Not in service',
    WRONG_NUMBER: 'Wrong Number',
    CONNECTED: 'Connected',
    NOT_INTERESTED: 'Not Interested',
    INTERESTED: 'Interested',
    GATEKEEPER: 'Gatekeeper',
    FOLLOW_UP_REQUIRED: 'Follow Up Required',
    BRUSH_OFF: 'Brush Off',
    MEETING_BOOKED: 'Meeting Booked',
    DISQUALIFIED: 'Disqualified',
    OTHER_ANSWERED: 'Other Answered (see notes)',
});

export function QuickAddPanel() {
    const dispatch = useDispatch();
    const platform = useSelector((state: RootState) => state.autodialerSettings.selectedPlatform);
    const currentDispositions = useSelector(selectCurrentDispositions(platform!));
    const isSaving = useSelector(selectIsSaving(platform!));

    const handleQuickAdd = (dispositionKey: string) => {
        if (platform) {
            const selectedDisposition = LIST_DISPOSITION_OPTIONS[dispositionKey as keyof typeof LIST_DISPOSITION_OPTIONS];
            if (!currentDispositions.includes(selectedDisposition)) {
                const newList = [...currentDispositions, selectedDisposition];
                dispatch(updateCurrentDispositions({ platform, dispositions: newList }));
            }
        }
    };

    const handleUseAllDefaults = () => {
        if (platform) {
            const allDispositions = Object.values(LIST_DISPOSITION_OPTIONS);
            const uniqueDispositions = [...new Set([...currentDispositions, ...allDispositions])];
            dispatch(updateCurrentDispositions({ platform, dispositions: uniqueDispositions }));
        }
    };

    // Only show if there are options to add
    const availableOptions = Object.entries(LIST_DISPOSITION_OPTIONS)
        .filter(([_, value]) => !currentDispositions.includes(value));

    if (availableOptions.length === 0) {
        return null;
    }

    return (
        <div className="lg:col-span-1">
            <div className="sticky top-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <Typography variant="largeParagraph" fontWeight="500" color="#1E40AF" className="mb-3">
                    Quick Add
                </Typography>
                <Typography variant="smallParagraph" color="#3B82F6" className="mb-4">
                    Common disposition options
                </Typography>
                <div className="space-y-2">
                    {availableOptions.map(([key, value]) => (
                        <button
                            key={key}
                            onClick={() => handleQuickAdd(key)}
                            className="w-full px-3 py-2 text-sm bg-white border border-blue-200 rounded-md hover:bg-blue-100 hover:border-blue-300 transition-colors text-left"
                            disabled={isSaving}
                        >
                            <Typography variant="smallParagraph" color="#1E40AF">
                                + {value}
                            </Typography>
                        </button>
                    ))}
                </div>
                <button
                    onClick={handleUseAllDefaults}
                    className="w-full mt-3 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    disabled={isSaving}
                >
                    <Typography variant="smallParagraph" color="white" fontWeight="500">
                        Add All Remaining
                    </Typography>
                </button>
            </div>
        </div>
    );
}